'use client'
import { useState, useRef } from 'react'
import { gsap } from 'gsap'
import OrcidIcon from 'public/icons/interface/orcid.svg'
import LocationIcon from 'public/icons/interface/location.svg'
import EmailIcon from 'public/icons/interface/email.svg'
import Arrow from 'public/icons/arrows/expanded-arrow.svg'
import { Tag, Typography } from 'src/components/design-system'
import { getOrcidLabel, getOrcidUrl } from 'src/_helpers/orcid'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Link from 'next/link'
import Image from 'next/image'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'

const normalizeLine = (value, locale) => {
  if (!value) return null
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    return getPreferredTranslation(value, locale)?.title || value.title || value.name || value.fullName || null
  }

  return String(value).trim()
}

const normalizeLines = (value, locale) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.map((line) => normalizeLine(line, locale)).filter(Boolean)
  }
  return String(value)
    .split(/\r?\n|\|/)
    .map((line) => line.trim())
    .filter(Boolean)
}

export function AuthorListItem({
  position,
  name,
  orcid,
  affiliations,
  address,
  email,
  imageSrc,
  detailUrl,
  className = '',
  desc,
  locale,
  onClick,
  showImagePlaceholder = false,
  tags = [],
}) {
  const orcidLink = getOrcidUrl(orcid)
  const orcidLabel = getOrcidLabel(orcid)
  const affiliationLines = normalizeLines(affiliations, locale)
  const translate = useTranslateEntry()
  const [open, setOpen] = useState(false)
  const contentRef = useRef(null)
  const wrapperRef = useRef(null)

  const toggle = () => {
    setOpen((prev) => !prev)

    if (!contentRef.current || !wrapperRef.current) return

    if (!open) {
      gsap.fromTo(
        wrapperRef.current,
        { height: 0 },
        {
          height: contentRef.current.scrollHeight,
          duration: 0.4,
          ease: 'power2.out',
          onComplete: () => {
            wrapperRef.current.style.height = 'auto'
          },
        },
      )
    } else {
      gsap.fromTo(
        wrapperRef.current,
        { height: wrapperRef.current.scrollHeight },
        {
          height: 0,
          duration: 0.35,
          ease: 'power2.inOut',
        },
      )
    }
  }

  const descId = `desc-${name?.replace(/\s+/g, '-').toLowerCase()}`
  const media = imageSrc ? (
    <Image src={imageSrc} alt={name || ''} width={309} height={372} />
  ) : (
    <div className="author-tile__placeholder" aria-hidden />
  )

  return (
    <div className={`flex flex-col gap-64 ${className}`.trim()}>
      {/* Zewnętrzna kolumna trzyma odstęp zdjęcie–treść (24px), wewnętrzna zostaje przy 64px
          — bez zdjęcia jest jedno dziecko, więc układ bez zdjęcia się nie zmienia */}
      <div className="group/author flex flex-col gap-24">
        {(imageSrc || showImagePlaceholder || tags.length > 0) && (
          // Odstępy zdjęcie–tag jak w ProductTile, żeby autor w wynikach wyszukiwania
          // równał się z kaflami publikacji
          <div className="flex flex-col gap-16">
            {(imageSrc || showImagePlaceholder) && (
              <div className="author-tile__image">
                {detailUrl ? (
                  <Link
                    href={detailUrl}
                    className="author-photo block cursor-pointer"
                    tabIndex={-1}
                    aria-hidden
                    onClick={onClick}
                  >
                    {media}
                  </Link>
                ) : (
                  media
                )}
              </div>
            )}

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-6">
                {tags.map((tag) => (
                  <Tag key={tag.label} label={tag.label} type={tag.type} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-64">
          {position && <Typography variant="h3-light">{position}</Typography>}

          <div className="flex flex-col gap-48">
            {(name || orcidLink) && (
              <div className="flex flex-col gap-12">
                {name && (
                  <Typography variant="h3-bold">
                    {detailUrl ? (
                      <Link
                        href={detailUrl}
                        className="hover:underline group-has-[.author-photo:hover]/author:underline"
                        onClick={onClick}
                      >
                        {name}
                      </Link>
                    ) : (
                      name
                    )}
                  </Typography>
                )}
                {orcidLink && (
                  <div className="flex items-center gap-8">
                    <OrcidIcon aria-hidden />
                    <a
                      href={orcidLink}
                      target="_blank"
                      rel="noreferrer"
                      className="group/orcid flex items-center gap-8"
                    >
                      <div className="h-20 w-px bg-dark-blue-dark-blue4" aria-hidden />
                      <Typography
                        variant="h4-semi"
                        className="underline decoration-1 underline-offset-2 group-hover/orcid:no-underline"
                      >
                        {orcidLabel}
                      </Typography>
                    </a>
                  </div>
                )}
              </div>
            )}

            {affiliationLines.length > 0 && (
              <div className="flex flex-col gap-4">
                {affiliationLines.map((line, index) => (
                  <Typography key={`${line}-${index}`} variant="h4-semi">
                    {line}
                  </Typography>
                ))}
              </div>
            )}

            {(address || email) && (
              <div className="flex flex-col gap-8">
                {address && (
                  <div className="flex items-center gap-6">
                    <LocationIcon aria-hidden />
                    <Typography variant="h4-semi">{address}</Typography>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-6">
                    <EmailIcon aria-hidden />
                    <a href={`mailto:${email}`}>
                      <Typography variant="h4-semi">{email}</Typography>
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {desc && (
        <div className="w-full">
          <button
            onClick={toggle}
            aria-expanded={open}
            aria-controls={descId}
            className="w-full flex items-center justify-end gap-x-8 border-b border-dark-blue-dark-blue1 py-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-dark-blue-dark-blue1"
          >
            <Typography variant="body3-normal" className="flex items-center gap-x-8">
              {open ? translate('T_LESS') : translate('T_MORE')}
            </Typography>

            <Arrow
              className={`transition-transform duration-300 ${open ? 'rotate-180' : 'rotate-0'}`}
              aria-hidden="true"
            />
          </button>

          <div ref={wrapperRef} id={descId} className="overflow-hidden h-0" aria-live="polite">
            <div ref={contentRef} className="pt-16">
              <Typography variant="body3-normal" color="text-basic-black">
                {desc}
              </Typography>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
